interface DominoTileProps {
  dominoId: string; // Format: "left-right" e.g., "6-4", "0-0", "3-3"
  faceDown?: boolean;
  selected?: boolean;
  onClick?: () => void;
  orientation?: 'horizontal' | 'vertical';
  size?: 'sm' | 'md' | 'lg';
  flipped?: boolean; // If true, swap the display order of the two halves
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
}

const SIZES = {
  sm: { width: 30, height: 60, dotSize: 5, gap: 2 },
  md: { width: 40, height: 80, dotSize: 7, gap: 3 },
  lg: { width: 50, height: 100, dotSize: 9, gap: 4 },
};

// Dot positions for each pip count (0-12 for Big 12)
// Default patterns are for vertical half-tiles (taller than wide)
const DOT_PATTERNS: Record<number, [number, number][]> = {
  0: [],
  1: [[0.5, 0.5]],
  2: [[0.25, 0.25], [0.75, 0.75]],
  3: [[0.25, 0.25], [0.5, 0.5], [0.75, 0.75]],
  4: [[0.25, 0.25], [0.75, 0.25], [0.25, 0.75], [0.75, 0.75]],
  5: [[0.25, 0.25], [0.75, 0.25], [0.5, 0.5], [0.25, 0.75], [0.75, 0.75]],
  6: [[0.25, 0.2], [0.75, 0.2], [0.25, 0.5], [0.75, 0.5], [0.25, 0.8], [0.75, 0.8]], // 2 cols x 3 rows (for vertical)
  7: [[0.25, 0.2], [0.75, 0.2], [0.25, 0.5], [0.5, 0.5], [0.75, 0.5], [0.25, 0.8], [0.75, 0.8]],
  8: [[0.25, 0.2], [0.5, 0.2], [0.75, 0.2], [0.25, 0.5], [0.75, 0.5], [0.25, 0.8], [0.5, 0.8], [0.75, 0.8]],
  9: [[0.25, 0.2], [0.5, 0.2], [0.75, 0.2], [0.25, 0.5], [0.5, 0.5], [0.75, 0.5], [0.25, 0.8], [0.5, 0.8], [0.75, 0.8]],
  10: [[0.2, 0.15], [0.5, 0.15], [0.8, 0.15], [0.2, 0.4], [0.5, 0.4], [0.8, 0.4], [0.2, 0.65], [0.8, 0.65], [0.35, 0.85], [0.65, 0.85]],
  11: [[0.2, 0.15], [0.5, 0.15], [0.8, 0.15], [0.2, 0.4], [0.5, 0.4], [0.8, 0.4], [0.2, 0.65], [0.5, 0.65], [0.8, 0.65], [0.35, 0.85], [0.65, 0.85]],
  12: [[0.2, 0.12], [0.5, 0.12], [0.8, 0.12], [0.2, 0.32], [0.5, 0.32], [0.8, 0.32], [0.2, 0.52], [0.5, 0.52], [0.8, 0.52], [0.2, 0.72], [0.5, 0.72], [0.8, 0.72]],
};

// Horizontal patterns for values that need different layout when tile is horizontal
// For horizontal half-tiles (wider than tall), 6 should be 2 rows x 3 cols
const DOT_PATTERNS_HORIZONTAL: Record<number, [number, number][]> = {
  6: [[0.2, 0.25], [0.5, 0.25], [0.8, 0.25], [0.2, 0.75], [0.5, 0.75], [0.8, 0.75]], // 3 cols x 2 rows
};

function DotPattern({ value, width, height, dotSize, isHorizontalHalf = false }: { value: number; width: number; height: number; dotSize: number; isHorizontalHalf?: boolean }) {
  // Use horizontal pattern for 6 when the half-tile is horizontal (wider than tall)
  const pattern = (isHorizontalHalf && DOT_PATTERNS_HORIZONTAL[value])
    ? DOT_PATTERNS_HORIZONTAL[value]
    : (DOT_PATTERNS[value] || []);

  return (
    <>
      {pattern.map(([x, y], i) => (
        <circle
          key={i}
          cx={x * width}
          cy={y * height}
          r={dotSize / 2}
          fill="#1a1a1a"
        />
      ))}
    </>
  );
}

export function DominoTile({
  dominoId,
  faceDown = false,
  selected = false,
  onClick,
  orientation = 'vertical',
  size = 'md',
  flipped = false,
  draggable = false,
  onDragStart,
  onDragEnd,
}: DominoTileProps) {
  const [rawTop, rawBottom] = dominoId.split('-').map(Number);
  // If flipped, swap the values so the "connecting" end is positioned correctly
  const topValue = flipped ? rawBottom : rawTop;
  const bottomValue = flipped ? rawTop : rawBottom;
  const dimensions = SIZES[size];

  const isHorizontal = orientation === 'horizontal';
  const width = isHorizontal ? dimensions.height : dimensions.width;
  const height = isHorizontal ? dimensions.width : dimensions.height;
  const halfHeight = height / 2;

  if (faceDown) {
    return (
      <div
        className={`rounded-md shadow-lg cursor-pointer transition-transform ${selected ? 'ring-2 ring-yellow-400 scale-105' : 'hover:scale-105'}`}
        style={{
          width,
          height,
          background: 'linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)',
          border: '2px solid #334155',
        }}
        onClick={onClick}
      />
    );
  }

  const svgElement = (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={`rounded-md transition-transform ${selected ? 'ring-2 ring-yellow-400 scale-105' : 'hover:scale-105'} ${draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}`}
      onClick={onClick}
      style={{ filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))' }}
    >
      {/* Domino background - ivory/cream color */}
      <rect
        x={0}
        y={0}
        width={width}
        height={height}
        rx={4}
        ry={4}
        fill="#F5F5DC"
        stroke="#8B7355"
        strokeWidth={2}
      />

      {/* Top half dots */}
      <g>
        {isHorizontal ? (
          <DotPattern value={topValue} width={width / 2} height={height} dotSize={dimensions.dotSize} isHorizontalHalf={true} />
        ) : (
          <DotPattern value={topValue} width={width} height={halfHeight} dotSize={dimensions.dotSize} isHorizontalHalf={false} />
        )}
      </g>

      {/* Divider line */}
      {isHorizontal ? (
        <line x1={width / 2} y1={4} x2={width / 2} y2={height - 4} stroke="#8B7355" strokeWidth={2} />
      ) : (
        <line x1={4} y1={halfHeight} x2={width - 4} y2={halfHeight} stroke="#8B7355" strokeWidth={2} />
      )}

      {/* Bottom half dots */}
      <g transform={isHorizontal ? `translate(${width / 2}, 0)` : `translate(0, ${halfHeight})`}>
        {isHorizontal ? (
          <DotPattern value={bottomValue} width={width / 2} height={height} dotSize={dimensions.dotSize} isHorizontalHalf={true} />
        ) : (
          <DotPattern value={bottomValue} width={width} height={halfHeight} dotSize={dimensions.dotSize} isHorizontalHalf={false} />
        )}
      </g>
    </svg>
  );

  // Wrap in div for drag support
  if (draggable) {
    return (
      <div
        draggable
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        style={{ display: 'inline-block' }}
      >
        {svgElement}
      </div>
    );
  }

  return svgElement;
}

export function DominoHand({
  dominoes,
  selectedDominoes = [],
  onDominoClick,
  faceDown = false,
  size = 'md',
  draggableDominoes = [],
  onDominoDragStart,
  onDominoDragEnd,
}: {
  dominoes: string[];
  selectedDominoes?: string[];
  onDominoClick?: (dominoId: string) => void;
  faceDown?: boolean;
  size?: 'sm' | 'md' | 'lg';
  draggableDominoes?: string[];
  onDominoDragStart?: (dominoId: string, e: React.DragEvent) => void;
  onDominoDragEnd?: (dominoId: string, e: React.DragEvent) => void;
}) {
  return (
    <div className="flex gap-2 flex-wrap">
      {dominoes.map((dominoId) => {
        const isDraggable = draggableDominoes.includes(dominoId);
        return (
          <DominoTile
            key={dominoId}
            dominoId={dominoId}
            faceDown={faceDown}
            selected={selectedDominoes.includes(dominoId)}
            onClick={() => onDominoClick?.(dominoId)}
            size={size}
            draggable={isDraggable}
            onDragStart={isDraggable ? (e) => onDominoDragStart?.(dominoId, e) : undefined}
            onDragEnd={isDraggable ? (e) => onDominoDragEnd?.(dominoId, e) : undefined}
          />
        );
      })}
    </div>
  );
}
